/**
 * Style Selector
 *
 * Select caption styles (Emotional, Factual, etc.)
 */

import { MessageSquare } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, Checkbox, Badge } from '@/components/v2/ui';
import type { CaptionStyle } from '@/lib/api/content-studio';

interface StyleSelectorProps {
  styles: CaptionStyle[];
  selectedStyles: string[];
  onToggle: (styleId: string) => void;
}

const STYLE_COLORS: Record<string, string> = {
  emotional: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300',
  factual: 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300',
  humorous: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
  urgency: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  storytelling: 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300',
};

export function StyleSelector({
  styles,
  selectedStyles,
  onToggle,
}: StyleSelectorProps) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          Caption-Stile
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {styles.map((style) => {
            const isSelected = selectedStyles.includes(style.id);
            const colorClass = STYLE_COLORS[style.id] || 'bg-neutral-100 text-neutral-700';

            return (
              <label
                key={style.id}
                className={`
                  flex items-start gap-3 p-4 rounded-lg border cursor-pointer
                  transition-all duration-200
                  ${
                    isSelected
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-neutral-200 dark:border-neutral-700 hover:border-neutral-300'
                  }
                `}
              >
                <Checkbox
                  checked={isSelected}
                  onChange={() => onToggle(style.id)}
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className={colorClass}>{style.name}</Badge>
                  </div>
                  <p className="text-xs text-neutral-500">{style.description}</p>
                </div>
              </label>
            );
          })}
        </div>

        {selectedStyles.length === 0 && (
          <p className="text-sm text-amber-600 mt-4">
            Bitte mindestens einen Stil auswählen
          </p>
        )}
      </CardContent>
    </Card>
  );
}
